import React, { ReactText, useEffect, useState } from 'react'
import { Item } from '@react-stately/collections'

import { Importer } from '../ContentSourcePicker/ContentSourcePicker'
import { Tabs } from 'components/BlazeTabs/Tabs'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ContentSourcePickerVariant } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/Types'
import { FromURL } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/FromURL'
import { FromText } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/FromText'
import { FromDoc } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/FromDoc'
import { FromFile } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/FromFile'
import Styles from './Step1.module.scss'
import { Heading } from 'blaze-ui/Heading/Heading'
import { BareArticle } from 'components/CampaignCreationModal/CampaignCreationModal'
import { ArrowLeft, LinkAngled, Upload } from 'blaze-ui/icons'
import File from 'blaze-ui/icons/20/File'
import EditContained from 'blaze-ui/icons/20/EditContained'
import { HighDetailedVoice } from 'blaze-ui/icons/24'
import { Text } from 'blaze-ui/Text/Text'
import { FromWebsiteText } from 'screens/BrandKits/components/BrandVoice/ContentSourcePicker/FromWebsiteText'
import AlertTriangle from 'blaze-ui/icons/20/AlertTriangle'
import { useHeaderTheme } from 'hooks/useHeaderTheme'

export type Step1Props = {
  onClose: () => void
  selectedImporter: Importer
  setSelectedImporter: (importer: Importer) => void
  sampleText: string
  setSampleText: (sampleText: string) => void
  availableImporters?: Importer[]
  errorMessage?: string
}

export const Step1: React.FC<React.PropsWithChildren<Step1Props>> = ({
  selectedImporter,
  onClose,
  setSelectedImporter,
  sampleText,
  setSampleText,
  errorMessage,
  availableImporters = [Importer.ScanWebsite, Importer.AddText, Importer.SelectDoc, Importer.UploadFiles],
}) => {
  const { isMobile } = useResponsiveDesign()

  useEffect(() => {
    if (!isMobile) return

    if (selectedImporter === Importer.ScanWebsite) {
      setUrlExamplesVisible(true)
      return
    }

    setUrlExamplesVisible(false)
  }, [selectedImporter, isMobile])

  const changeImporter = (key: ReactText) => {
    setSelectedImporter(key as Importer)
    setSampleText('')
  }
  const [doc, setDoc] = useState<BareArticle | undefined>()
  const [textExamplesVisible, setTextExamplesVisible] = useState(true)
  const [urlExamplesVisible, setUrlExamplesVisible] = useState(true)

  const tabItems = [
    {
      key: Importer.ScanWebsite,
      component: (
        <FromURL
          sampleText={sampleText}
          showExamples
          onChange={setSampleText}
          setExamplesVisible={setUrlExamplesVisible}
          examplesVisible={urlExamplesVisible}
          type="voice"
        />
      ),
      title: (
        <div className={Styles.tabContents}>
          {!isMobile && <LinkAngled />}
          <Text variant="smallList">Link Website</Text>
        </div>
      ),
    },
    {
      key: Importer.AddText,
      component: (
        <FromText
          sampleText={sampleText}
          setExamplesVisible={setTextExamplesVisible}
          examplesVisible={textExamplesVisible}
          onChange={setSampleText}
          variant={ContentSourcePickerVariant.BrandVoiceImport}
        />
      ),
      title: (
        <div className={Styles.tabContents}>
          {!isMobile && <EditContained />}
          <Text variant="smallList">Paste Text</Text>
        </div>
      ),
    },
    {
      key: Importer.PasteWebsiteText,
      component: <FromWebsiteText sampleText={sampleText} onChange={setSampleText} />,
      title: (
        <div className={Styles.tabContents}>
          {!isMobile && <EditContained />}
          <Text variant="smallList">Paste Text</Text>
        </div>
      ),
    },
    {
      key: Importer.SelectDoc,
      component: (
        <FromDoc
          onChange={(text, doc) => {
            setSampleText(text)
            setDoc(doc)
          }}
          selectedDoc={doc}
          fullWidth
        />
      ),
      title: (
        <div className={Styles.tabContents}>
          {!isMobile && <File />}
          <Text variant="smallList">Select Doc</Text>
        </div>
      ),
    },
    {
      key: Importer.UploadFiles,
      component: <FromFile onChange={setSampleText} />,
      title: (
        <div className={Styles.tabContents}>
          {!isMobile && <Upload />}
          <Text variant="smallList">Upload Files</Text>
        </div>
      ),
    },
  ].filter((item) => availableImporters.includes(item.key))

  const heading = isMobile ? 'Add content to analyze your brand' : 'Add content so we can analyze your brand'
  const theme = useHeaderTheme()

  return (
    <div className={Styles.Step1}>
      <div className={Styles.header} data-theme={isMobile ? theme : undefined}>
        {isMobile && (
          <div onClick={onClose}>
            <ArrowLeft size={20} color={'var(--dark-100)'} />
          </div>
        )}
        {!isMobile && !errorMessage && <HighDetailedVoice size={32} />}
        {!isMobile && errorMessage && <AlertTriangle size={32} />}
        <Heading level={isMobile ? 5 : 3}>{errorMessage || heading}</Heading>
      </div>

      <Tabs
        aria-label="Voice Options"
        selectedKey={selectedImporter}
        onSelectionChange={changeImporter}
        withEditorTopColor={isMobile}
        classNames={{ tab: Styles.tab }}
        animate
      >
        {tabItems.map((item) => (
          <Item key={item.key} title={item.title}>
            {selectedImporter === item.key && item.component}
          </Item>
        ))}
      </Tabs>
    </div>
  )
}
